import { Component, computed, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatIconModule } from '@angular/material/icon';
import { MatDialogRef } from '@angular/material/dialog';

import Resources from '@constants/resourses';
import { IBanner } from 'src/app/core/models/tenancy.interface';
import { IQuotesTariff } from 'src/app/core/models/quotesTariff.interface';
import { BannerPlacement } from 'src/app/core/enums/banner.enum';

import { ExclusiveTagComponent } from '../exclusive-tag/exclusive-tag.component';
import { PerUnitPipe } from 'src/app/shared/pipes/per-unit.pipe';

import { SessionService } from '@services/session.service';
import { TealiumUtagService } from '@services/utag.service';
import { UtilityService } from '@services/utility.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { RestapiService } from '@services/restapi.service';
import { getEVChargesText } from '@constants/quotes.utils';

@Component({
  selector: 'app-supplier-tariff',
  standalone: true,
  imports: [CommonModule, MatIconModule, ExclusiveTagComponent, PerUnitPipe],
  templateUrl: './supplier-tariff.component.html',
  styleUrl: './supplier-tariff.component.scss'
})
export class SupplierTariffComponent implements OnInit, OnChanges, OnDestroy {
  @Input('index') index: number;
  @Input('banner') banner: IBanner[] = [];
  @Input('supplierDetails') selectedQuote: IQuotesTariff;
  @Input('meerkatReward') meerkatReward: any;
  @Input('source') source: string;
  @Input('isSmallScreen') isSmallScreen: boolean;
  @Output('close') close = new EventEmitter<void>();

  @Input() set open(event: any) {
    if (event?.quote?.TariffId === this.selectedQuote?.TariffId) {
      if (event?.eventType === 'open_tariff') {
        this.openTariffDetails();
      } else if (event?.eventType === 'switch_now') {
        this.checkSwitchingDetails();
      }
    }
  }

  subscriptions: Subscription[] = [];
  exclusiveTabPlacements = BannerPlacement;

  email: string = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
  quoteData: any = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.QuotesData);

  savingPerYear: any = signal(0)
  savingPerMonth: any = computed(() => this.utilityService.smartFormatNumber(this.savingPerYear() / 12));

  id: any = {};
  tealiumData: any = {};

  isNegNumber = (num: number) => this.utilityService.isNegativeNumber(num);
  openPopup = (fileName: string, target?: string) => this.utilityService.openMessageDialog(fileName, target);

  content: string = '';
  isLoading: boolean = false;
  abVariant: string;
  ctmFlowVariant: string;
  correlationId: string;
  supplierRewards: any;

  constructor(
    private sessionService: SessionService,
    private utilityService: UtilityService,
    private tealiumService: TealiumUtagService,
    private googleAnalytics: GoogleAnalyticsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private restAPI: RestapiService
  ) {
    this.correlationId = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId);
    this.tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData) || {};
    // Get breakpoint for responsive design
    this.utilityService.getBreakpoint().subscribe((res: any) => {
      this.isSmallScreen = res;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['banner'] && changes['banner'].currentValue) {
      this.banner = changes['banner'].currentValue;
    }
  }

  ngOnInit(): void {
    this.abVariant = this.utilityService.getCookie(Resources.ABtestingKeys.LANDING_PAGE_VARIANT_COOKIE) || 'A';
    this.ctmFlowVariant = this.utilityService.getCookie(Resources.ABtestingKeys.CTM_FLOW_VARIANT_COOKIE) || 'A';

    const reward = this.banner?.filter((tab: IBanner) => tab.placetoDisplay === BannerPlacement.SupplierRewards && tab.status === 'active');
    const dynamicQuote = this.utilityService.findQuoteByFilterKey([this.selectedQuote], reward?.[0]?.dynamicContentUniqueKey);
    this.supplierRewards = dynamicQuote?.length > 0 ? reward : null;

    this.id = {
      quote: this.tealiumData['app_name'] + '_' + this.tealiumData['page_name'],
    }

    this.savingPerYear.set(Math.abs(this.selectedQuote?.SavingPerYear));

    // Initialize isSmallScreen if it's undefined
    if (this.isSmallScreen === undefined) {
      this.isSmallScreen = window.innerWidth <= 767;
    }
  }

  openSimplePopup(): void {
    const evDetail = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.EVDETAILS);
    const isOwnEV = evDetail && (evDetail?.registration || evDetail?.reg || evDetail?.dvlaData?.registrationNumber);


    this.utilityService.openSimpleMessageDialog(
      'Understanding Your Home and EV Cost Breakdown', 
      getEVChargesText(
        this.selectedQuote?.Fuels.Electricity?.EVCalculationData?.evPeakChargeAtHome || 0,
        this.selectedQuote?.Fuels.Electricity.EVCalculationData.evOffPeakChargeAtHome || 0,
        !!isOwnEV
      )
    );
  }

  // This method is used to get the image path for the TrustPilot score based on the supplier rating [NOT in use]
  // The rating is parsed as a float and compared to different ranges to determine the appropriate image [NOT in use]
  getTrustPilotScore(supplierRating: string): string {
    const rating = parseFloat(supplierRating);

    if (rating === 5) {
      return './assets/ctm-images/ratings/tp5Stars.svg';
    } else if (rating > 4 && rating < 5) {
      return './assets/ctm-images/ratings/tp4.5Stars.svg';
    } else if (rating === 4) {
      return './assets/ctm-images/ratings/tp4Stars.svg';
    } else if (rating > 3 && rating < 4) {
      return './assets/ctm-images/ratings/tp3.5Stars.svg';
    } else if (rating === 3) {
      return './assets/ctm-images/ratings/tp3Stars.svg';
    } else if (rating > 2 && rating < 3) {
      return './assets/ctm-images/ratings/tp2.5Stars.svg';
    } else if (rating === 2) {
      return './assets/ctm-images/ratings/tp2Stars.svg';
    } else if (rating > 1 && rating < 2) {
      return './assets/ctm-images/ratings/tp1.5Stars.svg';
    } else if (rating === 1) {
      return './assets/ctm-images/ratings/tp1Stars.svg';
    }
    return './assets/ctm-images/ratings/tp0Stars.svg';
  }

  /**
   * @description This method is used to get the contract details based on the contract type.
   * @param contractType - The type of contract (e.g., 'Fixed', 'Variable', etc.)
   * @return A string representing the contract details.
   */
  getContractDetails(quote: IQuotesTariff): string {
    if (quote?.TarrifType === 'Fixed') {
      return `Fixed for ${quote?.ContractLength} months`;
    } else if (quote?.TarrifType === 'Variable') {
      return 'Variable - which means prices may change';
    }
  }

  eventTrigger(target: string, from?: string): void {
    // Trigger the Tealium event for tariff details
    // 2 tealium trigger events are sent for the same action 
    this.tealiumData['event_action'] = 'onClick';
    this.tealiumData['event_category'] = `Tariff${this.index + 1}`;
    this.tealiumData['event_label'] = (from === 'tariffDetails') ? `tariffDetails_${target}` : target;

    this.tealiumService.linkEvent(this.tealiumData);

    // Revamped the tealium data
    if (target === 'openTariff') {
      this.tealiumData['event_action'] = 'sendBridgingPageViewedEvent';
      this.tealiumData['event_category'] = '';
      this.tealiumData['event_label'] = 'BridgingPageViewed';
    } else {
      this.tealiumData['event_action'] = 'sendClickedThroughEvent';
      this.tealiumData['event_category'] = '';
      this.tealiumData['event_label'] = 'ClickedThrough';
    }

    this.tealiumData['brand_code'] = this.selectedQuote?.SupplierName;
    this.tealiumData['brand_name'] = this.selectedQuote?.SupplierName;
    this.tealiumData['brand_position'] = this.index + 1;
    this.tealiumData['brand_price'] = this.selectedQuote?.EstimatedYearlyCost;
    this.tealiumData['energy_tariff_duration'] = this.selectedQuote?.PricesFixedUntil !== 'N/A' ? this.utilityService.getTotalMonthFromToday(this.selectedQuote?.PricesFixedUntil, 'month') + ' months' : this.selectedQuote?.PricesFixedUntil;

    this.tealiumService.linkEvent(this.tealiumData);

    // Track the event with Google Analytics
    this.googleAnalytics.track({
      supplier_name: this.selectedQuote?.SupplierName,
      tariff_name: this.selectedQuote?.TariffName,
      tariff_position: this.index + 1,
      abVariant: this.abVariant,
      ctmFlowVariant: this.ctmFlowVariant
    }, (target === 'openTariff') ? 'open_tariff_details' : 'switch_now');


    if (target === 'openTariff') {
      // Open the tariff details dialog
      this.openTariffDetails();
    } else {
      // Navigate to the application page to switch to the selected tariff
      this.checkSwitchingDetails();
    }
  }

  // This method is used to open the tariff details dialog
  openTariffDetails(): void {
    // Configure dialog properties
    const dialogProperties = {
      data: {
        index: this.index,
        banner: this.banner,
        selectedQuote: this.selectedQuote,
        meerkatReward: this.meerkatReward?.meerkatReward,
        noMeerkatReward: this.meerkatReward?.noMeerkatReward,
        isSmallScreen: this.isSmallScreen
      },
      panelClass: 'modalTyp2',
      position: { right: '0' },
      height: '100vh',
      width: (window.innerWidth > 767) ? '70%' : '100%',
    };

    // Open the dialog with the specified properties
    const dialogRef = this.utilityService.openMessageDialog(dialogProperties, 'tariffDetails') as MatDialogRef<any>;

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result: any) => {
        if (result) {
          this.eventTrigger('switchNow', 'tariffDetails');
        }
      })
    );
  }

  // This method is used to check the switching details is already in progress or not
  checkSwitchingDetails(): void {
    this.isLoading = true;
    const currentUsage = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CurrentUsage);
    this.subscriptions.push(
      this.restAPI.postAPI('/v1/plans/userSwitches', { correlationId: this.correlationId, fuelType: this.selectedQuote.fuelType?.toLowerCase() }).subscribe({
        next: (res: any) => {
          // Check if the response contains the switching details
          if (res?.switches?.length > 0) {
            const fuelType = (currentUsage?.electricity && currentUsage?.gas) ? Resources.EnergyType.BOTH : (currentUsage?.electricity ? Resources.EnergyType.ELECTRICITY : Resources.EnergyType.GAS);

            let switchDetail: any;
            if (fuelType === Resources.EnergyType.BOTH) {
              // switchDetail = res?.switches?.find((item: any) => item?.mpan === currentUsage?.mpan && item?.mprn === currentUsage?.mprn)?.[0];
              switchDetail = res?.switches?.find((item: any) => item?.mpan === currentUsage?.mpan && item?.switchStatus === 'In Progress');
            } else if (fuelType === Resources.EnergyType.ELECTRICITY) {
              switchDetail = res?.switches?.find((item: any) => item?.mpan === currentUsage?.mpan && item?.switchStatus === 'In Progress');
            } else {
              switchDetail = res?.switches?.find((item: any) => item?.mprn === currentUsage?.mprn && item?.switchStatus === 'In Progress');
            }

            // Check if the switch detail is found
            if (switchDetail) {
              this.isLoading = false;
              this.utilityService.openMessageDialog('quotesPage/quotePage-switchInProgress.md');
            } else {
              // If no switching details are found, navigate to the application page
              this.navigateToApplicationPage();
            }
          } else {
            // If no switching details are found, navigate to the application page
            this.navigateToApplicationPage();
          }
        }, error: (err: any) => {
          console.log('Error:', err);
          this.isLoading = false;
        }
      })
    );
  }

  // Navigate to the application page
  navigateToApplicationPage(): void {
    // Store the selected quote in session storage
    this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.SelectedQuote, this.selectedQuote);
    if (this.supplierRewards?.length > 0) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.RewardBannerId, this.supplierRewards[0]?.externalRewardId);
    } else {
      this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.RewardBannerId);
    }

    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.router.navigate(['/switch/user-details'], {
      queryParams: queryParams,
    });
  }

  // This method is used to close the tariff details dialog
  closeDialog(): void {
    // Emit the close event to notify the parent component
    this.close.emit();
  }

  // Generate safe ID for Switch Now button
  getSwitchNowId(): string {
    const baseId = this.id?.quote + '_TariffsYouCanSwitch_SwitchNow_';
    const tariffName = this.selectedQuote?.TariffName || '';
    const safeName = tariffName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
    return baseId + safeName;
  }

  // Unsubscribe from all subscriptions to prevent memory leaks
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
